<script setup lang="ts">
import VitaEmptyPlaceholder from "@/components/UI/VitaEmptyPlaceholder.vue";
import { computed, Ref, ref } from "vue";
import {
  PerfectScrollbar,
  PerfectScrollbarExpose,
} from "vue3-perfect-scrollbar";
import VitaButton from "./UI/VitaButton.vue";
import VitaInfoModal from "./UI/VitaInfoModal.vue";
import VitaBeruTimeline from "./VitaBeruTimeline.vue";
import { BeruCardFree, BeruCardTaken, BeruDetails } from "@/types/beru";
import VitaLoaderSpinner from "./UI/VitaLoaderSpinner.vue";
import store from "@/store/index";
import { AxiosError } from "axios";
import { ErrorResponseData } from "@/types";
import { addHours, differenceInMinutes, format } from "date-fns";
import ru from "date-fns/locale/ru";
import { useToast, POSITION } from "vue-toastification";

interface Props {
  open: boolean;
  card: BeruCardFree | BeruCardTaken | null;
}

const props = defineProps<Props>();
const emit = defineEmits(['toggleInfoModal', 'canceled']);
const toast = useToast();
const isDetailLoading = ref(false);
const detailError = ref('');
const timelineScroll: Ref<PerfectScrollbarExpose | null> = ref(null);
const showCancellationConfirm = ref(false);
const cancelInProgress = ref(false);

const scrollOptions = {
  minScrollbarLength: 60,
  wheelPropagation: false,
  suppressScrollX: true,
};

const beruDetail = computed<BeruDetails | null>(
  () => store.getters["beru/beruDetail"],
);

const onBeruModalOpen = async () => {
  if (!props.card) return;

  try {
    detailError.value = "";
    isDetailLoading.value = true;
    showCancellationConfirm.value = false
    store.commit("beru/setBeruDetail", null);
    await store.dispatch("beru/getBeruDetail", props.card.id);
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    detailError.value = err.status !== 500 && err.response?.data ? err.response.data.message : "Ошибка загрузки данных";
  } finally {
    isDetailLoading.value = false;
  }
};

const pharmTz = computed<number>(() => store.getters["data/pharmTz"] || 0);

// Получаем дату и время с учётом смещения таймзоны аптеки
const toPharmDate = (iso: string): Date => {
  const timeZone = pharmTz.value + new Date().getTimezoneOffset() / 60;
  return addHours(new Date(iso), timeZone);
};

const formatDate = (iso: string): string =>
  format(toPharmDate(iso), "d MMMM yyyy", { locale: ru });

const formatTime = (iso: string): string => format(toPharmDate(iso), "HH:mm");

const shiftDurationHours = computed<number>(() => {
  if (!beruDetail.value) return 0;
  return (
    differenceInMinutes(
      new Date(beruDetail.value.dateTo),
      new Date(beruDetail.value.dateFrom),
    ) / 60
  );
});

const dinnerDuration = computed<number>(() => {
  const hours = shiftDurationHours.value;
  let dinner = 0;
  if (hours >= 13) dinner = 1.5;
  else if (hours >= 9) dinner = 1;
  else if (hours >= 5) dinner = 0.5;
  return dinner;
});

// Длительность смены с учётом обеда
const shiftDuration = computed<number>(() => {
  if (!beruDetail.value) return 0;
  return shiftDurationHours.value - dinnerDuration.value;
});

// Cвободная ли потребность
const isShiftFree = computed<boolean>(
  () =>
    beruDetail.value?.toCancel === false &&
    beruDetail.value.armZortCanceledAt === null,
);

const onTimelineToggle = () => {
  requestAnimationFrame(() => {
    timelineScroll.value?.ps?.update();

    const el = timelineScroll.value?.ps?.element as HTMLElement;
    if (el) el.scrollTop = 0;
  });
};

const cancelBeruShift = async (id: string) => {
  try {
    cancelInProgress.value = true;
    await store.dispatch("beru/cancelShiftNeeds", id);

   toast.success("Успешно", {
      position: POSITION.BOTTOM_RIGHT,
    });
    emit("toggleInfoModal");
    emit("canceled");
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    toast.error(err.status === 500 ? "Ошибка загрузки данных" : err.response?.data?.message,
    { position: POSITION.BOTTOM_RIGHT}
    );
  } finally {
    isDetailLoading.value = false;
    cancelInProgress.value = false;
  }
}
</script>

<template>
  <vita-info-modal
    :open="open"
    @toggleInfoModal="emit('toggleInfoModal')"
    :modal-icon="false"
    modal-size="modal-secondary modal-beru-detail"
    :title="'Потребность № ' + card?.serialNumber"
    @open="onBeruModalOpen"
  >
    <PerfectScrollbar
      class="beru-detail-scroll rail-gap-vertical rail-thin rail-lightgray"
      :options="scrollOptions"
      ref="timelineScroll"
    >
      <VitaLoaderSpinner v-if="isDetailLoading" data-qa="beru-detail-loader" />
      <div class="empty-placeholder" v-else-if="detailError">
        <VitaEmptyPlaceholder
          qa-name="beru-detail-error"
          icon="isOffline"
          :message="detailError"
          description="При загрузке данных произошла ошибка. Попробуйте обновить страницу позже."
          icon-width="100"
          icon-height="100"
          :on-refresh-click="onBeruModalOpen"
        />
      </div>
      <div
        class="beru-detail"
        v-else-if="beruDetail && Object.keys(beruDetail).length > 0"
        data-qa="beru-detail-block"
      >
        <div class="beru-detail-badges">
          <ul>
            <li class="success" v-if="beruDetail.shiftCost" data-qa="beru-detail-badge-shift-cost">
              +{{ beruDetail.shiftCost }}
            </li>
            <li class="success" v-if="beruDetail.shiftCostRD" data-qa="beru-detail-badge-shift-cost-rd">
              ~РД {{ beruDetail.shiftCostRD }}
            </li>
          </ul>
          <div
            class="beru-detail-status"
            v-if="beruDetail.armZortCanceledAt"
            data-qa="beru-detail-status-canceled"
          >
            Отменена
          </div>
        </div>
        <div class="beru-detail-row">
          <div>
            <h2 v-if="beruDetail.pharm?.name" data-qa="beru-detail-title-pharm">Аптека №{{ beruDetail.pharm?.name }}</h2>
            <div
              class="beru-detail-interval"
              v-if="beruDetail.dateFrom && beruDetail.dateTo"
              data-qa="beru-detail-value-interval"
            >
              {{ formatTime(beruDetail.dateFrom) }} -
              {{ formatTime(beruDetail.dateTo) }}
            </div>
          </div>
          <div class="beru-detail-timings">
            <div v-if="shiftDuration" data-qa="beru-detail-timing-shift">
              <vita-icon name="clock-circle"/>
              <span>{{ `${shiftDuration.toLocaleString("ru-RU")}ч` }}</span>
            </div>
            <div v-if="dinnerDuration" data-qa="beru-detail-timing-dinner">
              <vita-icon name="dinner-circle"/>
              <span>{{ `${dinnerDuration.toLocaleString("ru-RU")}ч` }}</span>
            </div>
          </div>
        </div>
        <dl class="beru-detail-chars" data-qa="beru-detail-chars-block">
          <template v-if="beruDetail.dateFrom">
            <dt data-qa="beru-detail-label-date">Дата</dt>
            <dd data-qa="beru-detail-value-date">{{ formatDate(beruDetail.dateFrom) }}</dd>
          </template>

          <template v-if="beruDetail.pharm?.webAddress">
            <dt data-qa="beru-detail-label-pharm-address">Адрес аптеки №{{ beruDetail.pharm?.name }}</dt>
            <dd class="several-lines" data-qa="beru-detail-value-pharm-address">
              <span v-if="beruDetail.pharm?.cityName" data-qa="beru-detail-value-pharm-city">{{ beruDetail.pharm?.cityName }}, </span>
              <span v-if="beruDetail.pharm?.webAddress" data-qa="beru-detail-value-pharm-street">{{ beruDetail.pharm?.webAddress }}</span>
            </dd>
          </template>

          <template v-if="beruDetail.reasonForChange">
            <dt data-qa="beru-detail-label-reason">Причина</dt>
            <dd data-qa="beru-detail-value-reason">{{ beruDetail.reasonForChange }}</dd>
          </template>

          <template v-if="beruDetail.typeReasonForChange">
            <dt data-qa="beru-detail-label-reason-type">Вид причины</dt>
            <dd data-qa="beru-detail-value-reason-type">{{ beruDetail.typeReasonForChange }}</dd>
          </template>

          <template v-if="beruDetail.anchor && beruDetail.anchor?.name">
            <dt class="top-divider" data-qa="beru-detail-label-anchor">Якорная аптека</dt>
            <dd class="several-lines" data-qa="beru-detail-value-anchor">
              <span v-if="beruDetail.anchor?.name" data-qa="beru-detail-value-anchor-name">Аптека №{{ beruDetail.anchor?.name }},</span>
              <span v-if="beruDetail.anchor?.webAddress" data-qa="beru-detail-value-anchor-address">{{ beruDetail.anchor?.webAddress }}</span>
            </dd>
          </template>
        </dl>
        <vita-beru-timeline
          v-if="beruDetail.exchangeWeb?.length"
          :exchange-web="beruDetail.exchangeWeb"
          @toggle="onTimelineToggle"
        >
        </vita-beru-timeline>
        <div class="beru-detail-cancel" v-if="isShiftFree">
          <div class="beru-detail-confirm" v-if="showCancellationConfirm" data-qa="beru-detail-confirm-cancel-block">
            <div data-qa="beru-detail-confirm-cancel-text">Вы уверены, что хотите отменить потребность?</div>
            <div>
              <vita-button class="rounded no-shadow" color="red" width="inline" :btn-loading="cancelInProgress" @click="cancelBeruShift(beruDetail.id)"
                           data-qa="beru-detail-button-cancel-confirm">Да</vita-button>
              <vita-button class="rounded no-shadow" color="blue" width="inline" @click="showCancellationConfirm = false"
                           data-qa="beru-detail-button-cancel-reject">Нет</vita-button>
            </div>
          </div>
          <vita-button
          class="rounded no-shadow"
          width="inline"
          v-if="!showCancellationConfirm"
          @click="showCancellationConfirm = true"
          data-qa="beru-detail-button-cancel-open"
          >
            Отменить потребность
          </vita-button>
        </div>
      </div>
    </PerfectScrollbar>
  </vita-info-modal>
</template>

<style scoped lang="scss">
.beru-detail {
  padding: 30px 60px 60px;
  width: 556px;
  display: flex;
  flex-direction: column;
  flex: 1;

  h2 {
    word-break: break-word;
  }

  &-badges {
    display: flex;
    gap: 4px;

    ul {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      flex: 1;
      list-style: none;
      padding: 0;
      margin: 0;
    }

    li {
      padding: 4px 6px;
      background-color: darkgray;
      color: #ffffff;
      border-radius: 6px;
      overflow: hidden;
      font-weight: 500;
      font-size: 13px;
      line-height: 1.24;
      letter-spacing: 0;
      vertical-align: middle;

      &.success {
        background-color: #30b648;
      }
    }
  }

  &-status {
    font-weight: 500;
    font-size: 12px;
    line-height: 1.1;
    letter-spacing: 0;
    vertical-align: bottom;
    color: rgba(52, 59, 69, 0.6);
    border-radius: 12px;
    background-color: rgba(203, 207, 210, 0.2);
    padding: 5px 8px;
    overflow: hidden;
  }

  &-interval {
    font-weight: 500;
    font-size: 15px;
    line-height: 1.2;
    vertical-align: middle;

    &:not(:first-child) {
      margin-top: 2px;
    }
  }

  &-chars {
    &:not(:first-child) {
      margin-top: 20px;
    }

    dt {
      font-weight: 500;
      font-size: 12px;
      line-height: 1.2;
      letter-spacing: 0;
      vertical-align: middle;
      color: rgba(148, 156, 175, 1);

      &:not(:first-child) {
        margin-top: 8px;
      }
    }

    dd {
      font-weight: 500;
      font-size: 13px;
      line-height: 1.2;
      letter-spacing: 0;
      color: rgba(52, 59, 69, 1);
      word-break: break-word;
      display: flex;
      gap: 2px;

      &.several-lines {
        display: block;
      }

      &:not(:first-child) {
        margin-top: 2px;
      }
    }

    .top-divider {
      padding-top: 8px;

      &::before {
        content: "";
        display: block;
        width: 100%;
        height: 0.5px;
        background: rgba(148, 156, 175, 0.7);
        transform: translateY(-8px);
      }
    }
  }

  &-row {
    display: flex;
    justify-content: space-between;
    gap: 12px 16px;
    flex-wrap: wrap;

    &:not(:first-child) {
      margin-top: 16px;
    }
  }

  &-cancel {
    margin-top: auto;

    .btn {
      width: 100%;
      max-width: 75%;
      margin-left: auto;
    }
  }

  &-confirm {
    background-color: rgba(136, 136, 136, 0.1);
    border-radius: 8px;
    padding: 24px;
    padding-bottom: 20px;
    font-weight: 600;
    font-size: 18px;
    line-height: 100%;
    letter-spacing: 0;
    vertical-align: middle;
    display: flex;
    flex-direction: column;
    gap: 30px;

    > div:last-child {
      display: flex;
      align-self: flex-end;
      gap: 16px;
      max-width: 75%;

      .btn.red:not(:hover):not(:focus) {
        background-color: transparent;
      }

      .btn {
        min-width: 128px;
      }
    }
  }

  &-timings {
    display: flex;
    align-items: flex-end;
    gap: 8px;

    & > div {
      display: flex;
      align-items: center;
      gap: 2px;
      font-weight: 500;
      font-size: 12px;
      line-height: 100%;
      letter-spacing: 0;
      text-align: right;
      vertical-align: middle;
    }
  }
}

.empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 24px;
  height: 100%;

  &-title {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.2;
    letter-spacing: 0;
  }

  &-description {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: 0;
    color: rgba(136, 136, 136, 1);

    &:not(:first-child) {
      margin-top: 8px;
    }
  }
}

:deep(.loader-alt) {
  margin-top: -45px;
  position: absolute;
  inset: 0;
  height: auto;
}

:deep(.placeholder-message) {
  max-width: 250px;
}

.beru-detail-scroll {
  height: 593px;
  min-width: 556px;
  display: flex;
  flex-direction: column;
}
</style>
